<template>
  <admin-wrapper :has-buttons="false" :is-fetched="isFetched" :straight-title="$i18n.t('admin.notifications.title')">
    <template #top>
      <v-col cols="6" class="pa-0">
        <v-row no-gutters class="justify-start align-center" style="gap: 1rem">
          <v-col cols="auto">{{ $i18n.t('admin.notifications.title') }}:</v-col>
          <v-col cols="auto">
            <v-tooltip bottom color="font">
              <template #activator="{ on }">
                <span v-on="on">
                  <v-btn @click="openAddEditNotificationDialog(false)" color="success" x-small fab elevation="0">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </span>
              </template>
              {{ $i18n.t('admin.notifications.addNew') }}
            </v-tooltip>
          </v-col>
        </v-row>
        <div class="mt-6">
          <template>
            <v-row no-gutters>
              <v-col cols="12">
                <ribbon-list
                  :listItems="computedNotifications"
                  item-title-key="name"
                  item-subtitle-key="datasource_verbose_name"
                  no-items-translation-path="admin.notifications.noData"
                  style-type="rawHigher"
                  :toggleable="false"
                >
                  <template #itemTitle-prepend>
                    <v-icon class="mr-3">mdi-email</v-icon>
                  </template>
                  <template #itemButtons-replace="{ item }">
                    <span class="d-flex items-center">
                      <data-input
                        :data-type="{ name: 'switch' }"
                        dense
                        hide-details
                        :value="item.active"
                        @change="$event => changeActiveNotification($event, item.id)"
                      />
                      <dots-menu
                        :items="dotsItems"
                        @delete="openDeleteDialog(item)"
                        @edit="openAddEditNotificationDialog(item)"
                      />
                    </span>
                  </template>
                </ribbon-list>
              </v-col>
            </v-row>
          </template>
        </div>
        <main-input-dialog
          v-if="isDialogLoaded"
          :is-visible.sync="isDialogVisible"
          :title-translation="dialogTitle"
          :inputs="dialogInputs"
          dialog-width="40%"
          :agree-text="editItemId ? 'dialog.agreeSave' : 'dialog.agreeAdd'"
          :custom-is-valid="isContentFilled"
          :loading="isDialogLoading"
          @agree="handleAddEditNotification"
        >
          <template v-for="input in dialogInputs.filter(inp => !inp.isCustom)" #[`${input.name}.input`]>
            <v-col cols="6" :key="input.name">
              <data-input
                :key="input.name"
                @change="$event => handleChangeInput($event, input.name)"
                :class="`input__${input.name}`"
                v-model="dialogData[input.name]"
                v-bind="input"
                cols="12"
              />
            </v-col>
          </template>
          <template #[`eventType.input`]>
            <v-col cols="12">
              <v-form ref="radio-form" v-model="isRadioFormValid">
                <v-radio-group
                  v-model="editFeatureChangeTypeData.type"
                  @change="$event => handleChangeInput($event, 'FeatureUpdateEvent__radio')"
                >
                  <v-radio hide-details value="all" :disabled="!dialogData.datasource">
                    <template #label>
                      <v-row no-gutters class="align-center" style="gap: 1rem; min-height: 40px; width: 0">
                        <v-col cols="auto" class="label-text">{{ $i18n.t('admin.notifications.allChanges') }}</v-col>
                      </v-row>
                    </template>
                  </v-radio>
                  <v-radio hide-details value="attribute" :disabled="!dialogData.datasource">
                    <template #label>
                      <v-row no-gutters class="align-center" style="gap: 1rem; width: 0">
                        <v-col cols="auto" class="label-text">{{
                          $i18n.t('admin.notifications.attributeChanges')
                        }}</v-col>
                        <v-col cols="4">
                          <data-input
                            v-model="editFeatureChangeTypeData.name"
                            :disabled="editFeatureChangeTypeData.type !== 'attribute'"
                            v-bind="getAttributeInputProps()"
                          />
                        </v-col>
                      </v-row>
                    </template>
                  </v-radio>
                  <v-radio
                    hide-details
                    value="attribute_value"
                    :disabled="
                      !dialogData.datasource ||
                        !observedDatasourceAttributes.find(attr => attr.type === 'dict' && attr.allowed_values)
                    "
                  >
                    <template #label>
                      <v-row no-gutters class="align-center" style="gap: 1rem; flex-wrap: nowrap; width: 0">
                        <v-col cols="auto" class="label-text">{{
                          $i18n.t('admin.notifications.attributeChanges')
                        }}</v-col>
                        <v-col cols="4">
                          <data-input
                            v-model="editFeatureChangeTypeData.name"
                            @change="handleChangeAttributeRelation"
                            :disabled="editFeatureChangeTypeData.type !== 'attribute_value'"
                            v-bind="getAttributeInputProps(true)"
                          />
                        </v-col>
                        <v-col cols="auto" class="label-text">{{ $i18n.t('admin.notifications.changeInto') }}:</v-col>
                        <v-col style="min-width: 0">
                          <data-input
                            v-model="editFeatureChangeTypeData.values"
                            v-bind="dictAttributeValuesInputProps"
                          />
                        </v-col>
                      </v-row>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-form>
            </v-col>
          </template>
          <template #[`receiversTitle.input`]>
            <v-row no-gutters class="my-6">
              <v-col>
                <v-row no-gutters>
                  <v-col class="section-title pa-0">
                    {{ $i18n.t('admin.notifications.receiversTitle') }}
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col class="text-caption font-italic font-weight-light pa-0 text-left">
                    {{ $i18n.t('admin.notifications.receiversSubtitle') }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>
          <template #[`notificationEditor.input`]>
            <v-col cols="12">
              <v-row no-gutters class="mt-6">
                <v-col>
                  <v-row no-gutters>
                    <v-col class="section-title pa-0">
                      {{ $i18n.t('admin.notifications.contentTitle') }}
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col class="text-caption font-italic font-weight-light pa-0 text-left">
                      {{ $i18n.t('admin.notifications.contentBody') }}
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row no-gutters class="mt-6">
                <v-col cols="12">
                  <data-input
                    clearable
                    :dataType="{ name: 'text' }"
                    v-model="dialogData.subject"
                    :label="$i18n.t('admin.notifications.messageTitle')"
                    dense
                    outlined
                    :rules="[v => !!v || v == '0' || 'rules.required']"
                  ></data-input>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12" @keydown.enter="$event => $event.stopPropagation()">
                  <tiptap-vuetify
                    v-model="dialogData.content"
                    :extensions="extensions"
                    :editor-properties="{
                      disableInputRules: true,
                    }"
                    :placeholder="$i18n.t('admin.emailPlaceholder')"
                    :card-props="{ flat: true, outlined: true }"
                    style="text-align: left"
                  />
                </v-col>
              </v-row>
            </v-col>
          </template>
        </main-input-dialog>
      </v-col>
      <main-dialog
        :v-if="isDeleteDialogLoaded"
        :is-visible.sync="isDeleteDialogVisible"
        :loading="isDeleteDialogLoading"
        :title="$i18n.t('admin.notifications.deleteTitle')"
        :body="$i18n.t('admin.notifications.deleteBody')"
        @agree="handleDelete"
        agree-text="dialog.agreeDelete"
      />
    </template>
  </admin-wrapper>
</template>

<script>
import AdminWrapper from '@/components/AdminWrapper';
import MainInputDialog from '@/components/MainInputDialog';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';
import MainDialog from '@/components/MainDialog';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Blockquote,
  Paragraph,
  Heading,
  History,
  HardBreak,
} from 'tiptap-vuetify';

import { call, get } from 'vuex-pathify';

export default {
  name: 'NotificationsSettings',
  components: { AdminWrapper, MainInputDialog, MainDialog, DataInput, TiptapVuetify, RibbonList, DotsMenu },
  data: () => ({
    dotsItems: [{ name: 'edit' }, { name: 'delete' }],
    extensions: [
      Bold,
      Italic,
      Underline,
      Blockquote,
      Paragraph,
      [
        Heading,
        {
          options: {
            levels: [1, 2, 3],
          },
        },
      ],
      HardBreak,
      History,
    ],
    isFetched: false,
    eventTypeRadioValue: 'all',
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    isDialogLoaded: false,
    isDialogVisible: false,
    isDialogLoading: false,
    editItemId: null,
    dialogData: {},
    isRadioFormValid: false,
    editFeatureChangeTypeData: {
      type: 'all',
    },
    observedDatasourceAttributes: [],
    observedDatasourceAttributeValues: [],
  }),
  watch: {
    isDialogVisible(val) {
      if (val) return;
      this.dialogData = {};
      this.editFeatureChangeTypeData = {};
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    users: get('users/users'),
    groups: get('users/groups'),
    notifications: get('notifications/notifications'),
    computedNotifications() {
      return this.notifications.map(notification => ({
        ...notification,
        datasource_verbose_name: this.dataSources[notification.datasource]?.verbose_name ?? notification.datasource,
      }));
    },
    dialogTitle() {
      return this.$i18n.t(`admin.notifications.${this.editItemId ? 'editTitle' : 'addTitle'}`);
    },
    events() {
      return ['FeatureInsertEvent', 'FeatureUpdateEvent'].map(event => ({
        value: event,
        text: this.$i18n.t(`admin.notifications.events.${event}`),
      }));
    },
    isContentFilled() {
      if (!this.dialogData.content) return false;
      const tempElement = document.createElement('div');
      tempElement.innerHTML = this.dialogData.content;
      return !!tempElement.textContent;
    },
    eventInputs() {
      const observedDatasourceInput = {
        name: 'datasource',
        eventData: true,
        dataType: {
          name: 'select',
        },
        itemValue: 'name',
        itemText: 'verbose_name',
        items: Object.values(this.dataSources).filter(dt => dt.scope === 'core'),
        straightTitle: this.$i18n.t('admin.notifications.observedDatasource'),
        rules: [v => !!v || v == '0' || 'rules.required'],
      };
      switch (this.dialogData.event) {
        case 'FeatureInsertEvent': {
          return [observedDatasourceInput];
        }
        case 'FeatureUpdateEvent': {
          return [
            observedDatasourceInput,
            {
              name: 'eventType',
              isCustom: true,
              leftCol: null,
              cols: 12,
            },
          ];
        }
        default: {
          return [];
        }
      }
    },
    dictAttributeValuesInputProps() {
      return {
        dataType: {
          name: 'select',
        },
        multiple: true,
        chips: true,
        autoConvertSelect: false,
        itemText: 'name',
        itemValue: 'id',
        cols: 12,
        disabled: this.editFeatureChangeTypeData.type !== 'attribute_value',
        hideDetails: true,
        items: this.observedDatasourceAttributeValues,
        straightTitle: this.$i18n.t('admin.notifications.receiver'),
        rules: [v => !!v || v == '0' || 'rules.required'],
      };
    },
    receiversList() {
      return [
        ...Object.values(this.users).map(user => ({
          name: user.name,
          index: ['users', user.id],
        })),
        ...Object.values(this.groups).map(group => ({
          name: group.name,
          index: ['groups', group.id],
        })),
      ];
    },
    dialogInputs() {
      return [
        {
          name: 'name',
          dataType: {
            name: 'text',
          },
          straightTitle: this.$i18n.t('admin.notifications.name'),
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'event',
          dataType: {
            name: 'select',
          },
          items: this.events,
          straightTitle: this.$i18n.t('admin.notifications.event'),
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        ...this.eventInputs,
        {
          name: 'receiversTitle',
          isCustom: true,
          leftCol: null,
          cols: 12,
        },
        {
          name: 'receivers',
          dataType: {
            name: 'select',
          },
          align: 'baseline',
          multiple: true,
          chips: true,
          clearable: true,
          autoConvertSelect: false,
          itemText: 'name',
          itemValue: 'index',
          returnObject: false,
          items: this.receiversList,
          straightTitle: this.$i18n.t('admin.notifications.receiver'),
          rules: !this.dialogData.user_attribute ? [v => !!v || v == '0' || 'rules.required'] : [],
        },
        {
          name: 'user_attribute',
          dataType: {
            name: 'select',
          },
          align: 'baseline',
          clearable: true,
          autoConvertSelect: false,
          itemText: 'verbose_name',
          itemValue: 'name',
          returnObject: false,
          disabled: !this.dialogData.datasource,
          items: this.observedDatasourceAttributes.filter(
            attr => attr.type === 'relation' && attr.relation && attr.relation?.data_source === 'users_users'
          ),
          straightTitle: this.$i18n.t('admin.notifications.receiverAttribute'),
          rules: !this.dialogData.receivers?.length ? [v => !!v || v == '0' || 'rules.required'] : [],
        },
        {
          name: 'notificationEditor',
          isCustom: true,
          leftCol: null,
          cols: 12,
        },
      ];
    },
  },
  methods: {
    getDataSources: call('layers/getLayersMetadata'),
    getGroups: call('users/getGroups'),
    getUsers: call('users/getUsers'),
    ...call('notifications/*Notificatio*'),
    openAddEditNotificationDialog(item) {
      if (item) {
        this.editItemId = item.id;
        let copyData = { ...item };
        ['active', 'hoverable', 'listeners', 'id', 'options', 'attribute'].forEach(key => delete copyData[key]);
        copyData.receivers = Object.entries({ users: item.receivers.users ?? [], groups: item.receivers.groups ?? [] })
          .map(item => (item[1] ?? []).map(id => [item[0], id]))
          .flat();
        copyData.user_attribute = item.receivers.user_attribute;
        this.changeDatasource(item.datasource);
        this.dialogData = { ...copyData };
        if (item.options?.name) this.handleChangeAttributeRelation(item.options.name);
        this.editFeatureChangeTypeData = { ...item.options };
      } else {
        this.editItemId = null;
        this.editFeatureChangeTypeData = this.dialogData = {};
      }
      this.isDialogLoaded = this.isDialogVisible = true;
    },
    async changeActiveNotification(newValue, id) {
      try {
        await this.changeNotificationStatus({
          isActive: newValue,
          id,
        });
      } catch {
        //
      }
    },
    handleChangeAttributeRelation(ev) {
      const dtAttribute = this.observedDatasourceAttributes.find(attr => attr.name === ev);
      if (!dtAttribute) return;
      this.observedDatasourceAttributeValues = dtAttribute.allowed_values;
      this.editFeatureChangeTypeData.values = [];
    },
    openDeleteDialog(item) {
      this.editItemId = item.id;
      this.isDeleteDialogLoaded = this.isDeleteDialogVisible = true;
    },
    async handleDelete() {
      if (!this.editItemId) return;
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteNotification(this.editItemId);
        this.isDeleteDialogVisible = false;
      } catch {
        //
      } finally {
        this.isDeleteDialogLoading = false;
      }
    },
    async handleAddEditNotification() {
      this.isDialogLoading = true;
      let dialogDataCopy = { ...this.dialogData };
      ['datasource_verbose_name', 'user_attribute'].forEach(attr => delete dialogDataCopy[attr]);
      dialogDataCopy.receivers = (this.dialogData.receivers ?? []).reduce(
        (acc, item) => ({
          ...acc,
          ...(acc[item[0]] && { [item[0]]: [...acc[item[0]], item[1]] }),
        }),
        {
          groups: [],
          users: [],
          user_attribute: this.dialogData.user_attribute ?? null,
        }
      );
      dialogDataCopy.options = { ...this.editFeatureChangeTypeData };
      try {
        this.editItemId
          ? await this.updateNotification({
              id: this.editItemId,
              payload: dialogDataCopy,
            })
          : await this.addNotification(dialogDataCopy);
        this.isDialogVisible = false;
      } catch {
        //
      } finally {
        this.isDialogLoading = false;
      }
    },
    getAttributeInputProps(isRelation = false) {
      return {
        dataType: {
          name: 'select',
        },
        hideDetails: true,
        itemValue: 'name',
        itemText: 'verbose_name',
        cols: 12,
        items: this.observedDatasourceAttributes.filter(attr =>
          isRelation ? attr.type === 'dict' && attr.allowed_values : attr.type !== 'dict' || !attr.allowed_values
        ),
        straightTitle: this.$i18n.t('admin.notifications.event'),
        rules: [v => !!v || v == '0' || 'rules.required'],
      };
    },
    changeDatasource(datasourceName) {
      this.dialogData = {
        ...this.dialogData,
        user_attribute: null,
      };
      this.observedDatasourceAttributes = this.dataSources[datasourceName]?.attributes_schema.attributes ?? [];
      this.observedDatasourceAttributeValues = [];
      this.editFeatureChangeTypeData = this.dialogData.event === 'FeatureUpdateEvent' ? { type: 'all' } : {};
      this.$refs['radio-form']?.resetValidation();
    },
    handleChangeInput(event, name) {
      switch (name) {
        case 'event': {
          this.editFeatureChangeTypeData = this.dialogData.event === 'FeatureUpdateEvent' ? { type: 'all' } : {};
          break;
        }
        case 'FeatureUpdateEvent__radio': {
          this.editFeatureChangeTypeData = {
            type: this.editFeatureChangeTypeData.type,
          };
          this.$refs['radio-form']?.resetValidation();
          break;
        }
        case 'datasource': {
          this.changeDatasource(event);
          break;
        }
        default: {
          break;
        }
      }
    },
    async init() {
      this.isFetched = false;
      try {
        await Promise.all([
          this.getGroups(),
          this.getUsers(),
          this.getDataSources({ with_attributes_schema: true, scope: 'core' }),
          this.getNotifications(),
        ]);
        this.isFetched = true;
      } catch {
        //
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-input--switch) {
  margin-top: 0 !important;
}

.input__receivers :deep(.v-select__selections) {
  flex-wrap: wrap !important;
  row-gap: 0.5rem;
}
.label-text {
  font-size: 14px;
  color: #202124;
}

.section-title {
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
}

:deep(.v-radio--is-disabled) .label-text {
  opacity: 0.5;
}
</style>
